import React, { useState, useEffect } from 'react';
import { Stack, Text, Box, Group, SegmentedControl } from '@mantine/core';
import { Grid3X3, Move, FileText } from 'lucide-react';
import { AddNoteButton, NotesGrid, SearchNotesFilter } from '../notes';
import { useNavigation } from '../../contexts/NavigationContext';
import { DocumentNotesMode } from './DocumentNotesMode';
import { useNotes } from '../../hooks/useNotes';
import { useUser } from '../../contexts/UserContext';

const LAYOUT_MODE_STORAGE_KEY = 'symphonyTodo.notes.layoutMode';

export const NotesMode: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [focusNoteId, setFocusNoteId] = useState<number | null>(null);
  const { isMobile } = useNavigation();
  const { currentUser } = useUser();
  const { data: notes = [] } = useNotes(currentUser?.email || '');
  
  // Initialize layout mode from localStorage with fallback to 'organized'
  // Force organized layout on mobile
  const [layoutMode, setLayoutMode] = useState<'organized' | 'free' | 'document'>(() => {
    if (isMobile) {
      return 'organized';
    }
    try {
      const savedMode = localStorage.getItem(LAYOUT_MODE_STORAGE_KEY);
      return (savedMode === 'free' || savedMode === 'organized' || savedMode === 'document') ? savedMode : 'organized';
    } catch {
      return 'organized';
    }
  });

  // Force organized layout on mobile whenever isMobile changes
  useEffect(() => {
    if (isMobile && layoutMode !== 'organized') {
      setLayoutMode('organized');
    }
  }, [isMobile, layoutMode]);

  // Save layout mode to localStorage whenever it changes
  // Prevent layout mode changes on mobile
  const handleLayoutModeChange = (newMode: string) => {
    if (isMobile) {
      return; // Prevent layout mode changes on mobile
    }
    const mode = newMode as 'organized' | 'free' | 'document';
    setLayoutMode(mode);
    try {
      localStorage.setItem(LAYOUT_MODE_STORAGE_KEY, mode);
    } catch (error) {
      console.warn('Failed to save layout mode to localStorage:', error);
    }
  };

  return (
      <Box 
        style={{ 
          height: `calc(100vh - ${isMobile ? '60px' : '70px'})`, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
      <Box p={isMobile ? "xs" : "sm"} style={{ borderBottom: '1px solid var(--app-border)' }}>
        <Stack gap={isMobile ? 4 : 8} align="center" style={{ width: '100%' }}>
          {isMobile ? (
            // Mobile: All controls in a single row
            <Box style={{ 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: '8px',
              marginTop: '8px'
            }}>
              <AddNoteButton 
                onNoteCreated={(noteId) => setFocusNoteId(noteId)}
                isMobile={true}
              />
              <SearchNotesFilter
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </Box>
          ) : (
            // Desktop: Enhanced control bar with all controls
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '8px 12px',
                border: '2px solid var(--app-border)',
                borderRadius: '12px',
                backgroundColor: 'var(--app-surface)',
                width: 'fit-content',
                minWidth: '400px',
                height: '56px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              <AddNoteButton 
                onNoteCreated={(noteId) => setFocusNoteId(noteId)}
                isMobile={false}
              />
              
              <Box style={{
                width: '1px',
                height: '24px',
                backgroundColor: 'var(--app-border)',
                opacity: 0.5
              }} />
              
              <SearchNotesFilter
                value={searchQuery}
                onChange={setSearchQuery}
              />
              
              <Box style={{
                width: '1px',
                height: '24px',
                backgroundColor: 'var(--app-border)',
                opacity: 0.5
              }} />
              
              <SegmentedControl
                value={layoutMode}
                onChange={handleLayoutModeChange}
                data={[
                  {
                    value: 'organized',
                    label: (
                      <Group gap="xs" wrap="nowrap">
                        <Grid3X3 size={16} />
                        <Text size="sm">Organized</Text>
                      </Group>
                    ),
                  },
                  {
                    value: 'free',
                    label: (
                      <Group gap="xs" wrap="nowrap">
                        <Move size={16} />
                        <Text size="sm">Free</Text>
                      </Group>
                    ),
                  },
                  {
                    value: 'document',
                    label: (
                      <Group gap="xs" wrap="nowrap">
                        <FileText size={16} />
                        <Text size="sm">Document</Text>
                      </Group>
                    ),
                  },
                ]}
                style={{ flexShrink: 0, minWidth: 'fit-content' }}
              />
            </Box>
          )}
        </Stack>
      </Box>


      <Box style={{ flex: 1, overflow: 'hidden' }}>
        {layoutMode === 'document' ? (
          <Box 
            p={isMobile ? "xs" : "md"} 
            style={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <DocumentNotesMode 
              notes={notes} 
              searchQuery={searchQuery} 
              focusNoteId={focusNoteId}
              onNoteFocused={() => setFocusNoteId(null)}
            />
          </Box>
        ) : (
          <NotesGrid 
            searchQuery={searchQuery} 
            layoutMode={layoutMode as 'organized' | 'free'}
            focusNoteId={focusNoteId}
            onNoteFocused={() => setFocusNoteId(null)}
          />
        )}
      </Box>
    </Box>
  );
};
